/**
 * Layout Renderer
 * 
 * Given a PortalLayout and the available SnowAttributes from task definitions,
 * this generates the Wizard steps with custom sections, field ordering, and
 * column layouts as configured in the layout editor.
 */

import React from 'react';
import {
	Box,
	ColumnLayout,
	Container,
	Header,
	SpaceBetween,
	Table,
	WizardProps,
} from '@amzn/awsui-components-console';
import { isEqual } from 'lodash';
import { SnowAttribute, TaskDefinition } from '@amzn/snow-nxt-workflow-service-typescript-client';

import { PortalLayout, LayoutSection } from './layoutSchema.types';
import { REVIEW_ONLY_FIELD_IDS, SYSTEM_FIELD_IDS } from './layoutConstants';
import { isWorkflowScopedAttribute } from 'src/utils/attributeTypeUtils';
import SNOWFormAttribute from 'src/components/commonComponents/SNOWFormAttribute/SNOWFormAttribute';
import { KeyValue } from 'src/components/commonComponents/KeyValue';
import { useFormContext } from 'react-hook-form';
import DiffReviewForm from 'src/components/selfServeWorkflows/requirementsPortal/steps/DiffReviewForm';
import { SubWorkflowTaskGroup } from 'src/components/selfServeWorkflows/requirementsPortal/subWorkflowRequirementUtils';

interface LayoutRendererProps {
	layout: PortalLayout;
	taskDefinitions: TaskDefinition[];
	subWorkflowGroups?: SubWorkflowTaskGroup[];
	workflowName?: string;
	workflowIntention?: string;
	previousValues?: Record<string, unknown>;
	isIBFR?: boolean;
	prefilledValues?: any;
	handleAutoPopulation?: (attributeId: string) => void;
	autopopulatingAttributes?: Set<string>;
}

interface AttributeMeta {
	attribute: SnowAttribute;
	/** Null for workflow-level/system attributes; task id for task-specific ones. */
	taskId: string | null;
	/** Display name of the owning task, if task-specific. */
	taskName: string | null;
	/** Name of the sub-workflow the owning task belongs to, if any. */
	subWorkflowName?: string;
}

/**
 * Build a lookup of all available attributes. Keys must match the ids used in
 * the layout schema so `attributeMap.get(field.attributeId)` resolves the same
 * way as it does in the palette:
 *   - Workflow-scoped (Global / WorkflowSpecific) & system fields: keyed by raw
 *     `attributeId`.
 *   - Task-specific (TaskSpecificAttribute): keyed by
 *     `"{taskId}.{attributeId}"`, mirroring LayoutEditorPage's palette ids.
 *
 * For common attrs a task can preset a `value`, which would make the attribute
 * appear "review-only" and hide it from the form. The default portal's
 * `filterWorkflowLevelRequirements` handles this by only keeping the empty-value
 * copy; we match that behavior by preferring the empty-value version whenever
 * the same workflow-scoped attribute appears in multiple tasks.
 */
function buildAttributeMap(
	taskDefinitions: TaskDefinition[],
	subWorkflowGroups: SubWorkflowTaskGroup[] = []
): Map<string, AttributeMeta> {
	const map = new Map<string, AttributeMeta>();
	const subWorkflowNameByTaskId = new Map(
		subWorkflowGroups.flatMap((group) => group.taskDefinitions.map((task) => [task.taskId, group.workflowName] as const))
	);

	taskDefinitions.forEach(task => {
		const taskName = task.taskName || task.taskId || null;
		(task.taskInputs || []).forEach(attr => {
			if (!attr.attributeId) return;
			if (isWorkflowScopedAttribute(attr)) {
				const existing = map.get(attr.attributeId);
				if (!existing) {
					map.set(attr.attributeId, { attribute: attr, taskId: null, taskName: null });
				} else if (existing.attribute.value && !attr.value) {
					// Prefer the empty-value copy so the field renders as an editable input,
					// matching the default portal's filterWorkflowLevelRequirements behavior.
					map.set(attr.attributeId, { attribute: attr, taskId: null, taskName: null });
				}
			} else {
				const key = `${task.taskId}.${attr.attributeId}`;
				if (!map.has(key)) {
					map.set(key, {
						attribute: attr,
						taskId: task.taskId || null,
						taskName,
						subWorkflowName: subWorkflowNameByTaskId.get(task.taskId),
					});
				}
			}
		});
	});

	SYSTEM_FIELD_IDS.forEach(id => {
		if (!map.has(id)) {
			const systemAttr: any = {
				attributeId: id,
				attributeName: id === 'EntityName' ? 'Entity Name' : id === 'EntityType' ? 'Entity Type' : id,
				dataType: 'STRING',
			};
			if (id === 'Region') {
				systemAttr.options = ['NA', 'EU', 'JP'];
				systemAttr.regex = '(NA|EU|JP)';
				systemAttr.description = 'The region in which the entity is created. Must be NA | EU | JP';
				systemAttr.helpText = 'NA | EU | JP';
			} else if (id === 'EntityName') {
				systemAttr.regex = '^\\S+$';
				systemAttr.description = 'The Entity Name of the Node';
				systemAttr.helpText = 'Eg: ABC1';
			} else if (id === 'EntityType') {
				systemAttr.regex = '^\\S+$';
				systemAttr.description = 'The Entity type of the Node';
				systemAttr.helpText = 'Eg: fc';
			}
			map.set(id, { attribute: systemAttr, taskId: null, taskName: null });
		}
	});

	return map;
}

/**
 * Build the display label for a placed field. Honors the user's custom
 * `field.label` override; otherwise falls back to the attribute name. For
 * task-specific attributes the owning task name is always appended as a suffix
 * so users can tell them apart when they sit next to common attributes inside
 * the same custom section. If the label already ends with `[TaskName]` the
 * suffix is not duplicated (this covers older layouts and any user-authored
 * labels that already tag the task).
 */
function getPlacedFieldLabel(
	field: { label?: string },
	attribute: SnowAttribute,
	taskName: string | null,
	subWorkflowName?: string,
): string {
	const base = field.label || attribute.attributeName || attribute.attributeId || '';
	if (!taskName) return base;
	const suffix = subWorkflowName ? `[${subWorkflowName}][${taskName}]` : `[${taskName}]`;
	if (base.endsWith(suffix)) return base;
	return `${base} ${suffix}`;
}

function renderSection(
	section: LayoutSection,
	attributeMap: Map<string, AttributeMeta>,
	isIBFR: boolean,
	prefilledValues: any,
	handleAutoPopulation?: (attributeId: string) => void,
	autopopulatingAttributes?: Set<string>,
): React.ReactNode {

	const renderField = (field: typeof section.fields[number]) => {
		const meta = attributeMap.get(field.attributeId);
		if (!meta) return null;

		const { attribute, taskId, taskName, subWorkflowName } = meta;
		const formAttributeId = `${taskId || 'EntityData'}.${attribute.attributeId}`;
		const isAutopopulating = autopopulatingAttributes?.has(formAttributeId) || false;
		const isOverridable = (attribute as any).isOverridable;
		const hasOptions = attribute.options && attribute.options.length > 0;
		const isReviewOnly = (!hasOptions && Boolean(attribute.value) && !isOverridable)
			|| REVIEW_ONLY_FIELD_IDS.includes(attribute.attributeId!);

		if (isReviewOnly) return null;

		const displayName = getPlacedFieldLabel(field, attribute, taskName, subWorkflowName);

		if (isIBFR) {
			let prefilledValue = '';
			if (attribute.attributeId !== undefined) {
				prefilledValue = prefilledValues?.[attribute.attributeId];
			}

			return (
				<SNOWFormAttribute
					key={field.attributeId}
					attributeId={formAttributeId}
					attributeName={displayName}
					description={attribute.description}
					dataType={attribute.dataType}
					placeholder={attribute.helpText}
					regex={attribute.regex}
					mandatory={
						(attribute.mandatory === false || prefilledValue !== undefined || attribute.attributeId === 'metadata' || attribute.attributeId === 'keys')
							? false
							: true
					}
					options={attribute.options}
					prefetchedValue={prefilledValue}
					handleAutoPopulation={handleAutoPopulation}
					isAutopopulating={isAutopopulating}
					isOverridable={isOverridable}
				/>
			);
		}

		return (
			<SNOWFormAttribute
				key={field.attributeId}
				attributeId={formAttributeId}
				attributeName={displayName}
				description={attribute.description}
				dataType={attribute.dataType}
				placeholder={attribute.helpText}
				regex={attribute.regex}
				mandatory={attribute.mandatory !== false}
				options={attribute.options}
				prefetchedValue={attribute.value || undefined}
				handleAutoPopulation={handleAutoPopulation}
				isAutopopulating={isAutopopulating}
				isOverridable={isOverridable}
			/>
		);
	};

	const chunks: { type: 'grid' | 'full'; fields: typeof section.fields }[] = [];
	for (const field of section.fields) {
		if (field.fullWidth) {
			chunks.push({ type: 'full', fields: [field] });
		} else {
			const lastChunk = chunks[chunks.length - 1];
			if (lastChunk && lastChunk.type === 'grid') {
				lastChunk.fields.push(field);
			} else {
				chunks.push({ type: 'grid', fields: [field] });
			}
		}
	}

	return (
		<Container key={section.id} header={<Header>{section.title}</Header>}>
			<SpaceBetween size="l">
				{chunks.map((chunk, idx) =>
					chunk.type === 'grid' ? (
						<ColumnLayout key={idx} columns={section.columns} variant="default">
							{chunk.fields.map(renderField)}
						</ColumnLayout>
					) : (
						chunk.fields.map(renderField)
					)
				)}
			</SpaceBetween>
		</Container>
	);
}

function ColumnLayoutReviewSection({ title, columns, requirements }: {
	title: string;
	columns: 1 | 2 | 3;
	requirements: SnowAttribute[];
}) {
	const { watch } = useFormContext();
	const formData = watch();
	const entityData = formData.EntityData ?? {};

	const formatValue = (value: unknown): string => {
		if (value === null || value === undefined || value === '') return '—';
		if (typeof value === 'string') return value;
		return JSON.stringify(value);
	};

	const findValue = (attributeId: string): unknown => {
		if (formData[attributeId] != null && formData[attributeId] !== '') return formData[attributeId];
		if (entityData[attributeId] != null && entityData[attributeId] !== '') return entityData[attributeId];
		for (const key of Object.keys(formData)) {
			if (typeof formData[key] === 'object' && formData[key] !== null && !Array.isArray(formData[key])) {
				if (formData[key][attributeId] != null && formData[key][attributeId] !== '') return formData[key][attributeId];
			}
		}
		return undefined;
	};

	return (
		<Container header={<Header>{title}</Header>}>
			<ColumnLayout columns={columns} variant="text-grid" borders="horizontal">
				{requirements.map(attr => {
					const value = findValue(attr.attributeId!);
					return (
						<KeyValue label={attr.attributeName!} key={attr.attributeId!}>
							{formatValue(value)}
						</KeyValue>
					);
				})}
			</ColumnLayout>
		</Container>
	);
}

/**
 * Renders a mixed-scope diff review table for unplaced attributes.
 *
 * Unlike DiffReviewForm — which scopes both current and previous value lookups
 * to a single taskId — this component resolves each row's scope from the
 * attribute's own AttributeMeta. That lets common (EntityData) and task-specific
 * attributes share one container while still comparing against the correct slice
 * of `previousValues`.
 */
interface UnplacedDiffRow {
	attributeId: string;
	label: string;
	currentValue: unknown;
	previousValue: unknown;
	hasChanged: boolean;
}

function UnplacedDiffReviewSection({
	header,
	items,
	previousValues,
}: {
	header: string;
	items: AttributeMeta[];
	previousValues?: Record<string, unknown>;
}) {
	const { getValues } = useFormContext();
	const formData = getValues();

	const columnDefinitions = [
		{ id: 'attributeName', header: '', cell: (item: UnplacedDiffRow) => item.label },
		{
			id: 'previousValue',
			header: 'Existing',
			cell: (item: UnplacedDiffRow) => (
				<Box color={item.hasChanged ? 'text-status-error' : undefined}>
					{item.previousValue == null ? '-' : typeof item.previousValue === 'string' ? item.previousValue : JSON.stringify(item.previousValue)}
				</Box>
			),
		},
		{
			id: 'newValue',
			header: 'New',
			cell: (item: UnplacedDiffRow) => (
				<Box color={item.hasChanged ? 'text-status-success' : undefined}>
					{item.currentValue == null ? '-' : typeof item.currentValue === 'string' ? item.currentValue : JSON.stringify(item.currentValue)}
				</Box>
			),
		},
	];

	const rows: UnplacedDiffRow[] = items
		// Match DiffReviewForm: attributes with a preset backend value aren't editable.
		.filter(({ attribute }) => !attribute.value)
		.map(({ attribute, taskId, taskName, subWorkflowName }) => {
			const attributeId = attribute.attributeId!;
			const scopeKey = taskId || 'EntityData';
			const currentScope = (formData?.[scopeKey] as Record<string, unknown> | undefined) ?? {};
			const previousScope = (previousValues?.[scopeKey] as Record<string, unknown> | undefined) ?? {};
			const currentValue = formData?.[attributeId] ?? currentScope[attributeId];
			const previousValue = previousScope[attributeId];

			return {
				attributeId,
				label: getPlacedFieldLabel({}, attribute, taskName, subWorkflowName),
				currentValue,
				previousValue,
				hasChanged: !isEqual(currentValue, previousValue),
			};
		});

	return (
		<Container header={<Header>{header}</Header>}>
			<Table
				columnDefinitions={columnDefinitions}
				items={rows}
				variant="embedded"
				stripedRows
				wrapLines
			/>
		</Container>
	);
}

/**
 * Generates Wizard steps from a saved PortalLayout.
 * Falls back gracefully if fields referenced in the layout no longer exist in the definition.
 */
export function generateStepsFromLayout({
	layout,
	taskDefinitions,
	subWorkflowGroups,
	workflowName,
	workflowIntention,
	previousValues,
	isIBFR = false,
	prefilledValues = {},
	handleAutoPopulation,
	autopopulatingAttributes,
}: LayoutRendererProps): WizardProps.Step[] {
	const attributeMap = buildAttributeMap(taskDefinitions, subWorkflowGroups);

	// A field renders if the attribute is known and not review-only. Sections
	// with zero renderable fields are dropped; steps with zero renderable
	// sections are dropped entirely so users don't see empty wizard pages.
	const isFieldRenderable = (fieldAttributeId: string): boolean => {
		const meta = attributeMap.get(fieldAttributeId);
		if (!meta) return false;
		const { attribute } = meta;
		const hasOptions = attribute.options && attribute.options.length > 0;
		const isReviewOnly = (!hasOptions && Boolean(attribute.value) && !(attribute as any).isOverridable)
			|| REVIEW_ONLY_FIELD_IDS.includes(attribute.attributeId!);
		return !isReviewOnly;
	};

	const isSectionRenderable = (section: LayoutSection): boolean =>
		section.fields.some(f => isFieldRenderable(f.attributeId));

	const formSteps: WizardProps.Step[] = layout.steps
		.map(step => ({
			...step,
			sections: step.sections.filter(isSectionRenderable),
		}))
		.filter(step => step.sections.length > 0)
		.map(step => ({
			title: step.title,
			description: step.description,
			content: (
				<SpaceBetween size="l">
					{step.sections.map(section =>
						renderSection(section, attributeMap, isIBFR, prefilledValues, handleAutoPopulation, autopopulatingAttributes)
					)}
				</SpaceBetween>
			),
		}));

	const placedFieldIds = new Set<string>();
	layout.steps.forEach(step =>
		step.sections.forEach(section =>
			section.fields.forEach(field => placedFieldIds.add(field.attributeId))
		)
	);

	// Collect all unplaced attributes into a single list. Task-specific attrs are
	// distinguished by the [TaskName] suffix on the label, not a separate section,
	// mirroring how placed task-specific fields render.
	// Persistence still uses EntityData.<attrId> and <taskId>.<attrId> paths, so
	// values save the same way as the default RequirementsPortal — only rendering
	// is driven by the layout.
	const unplacedAttributes: AttributeMeta[] = [];

	attributeMap.forEach((meta, key) => {
		if (placedFieldIds.has(key)) return;
		const attribute = meta.attribute;
		const hasOptions = attribute.options && attribute.options.length > 0;
		const isReviewOnly = (!hasOptions && Boolean(attribute.value) && !(attribute as any).isOverridable)
			|| REVIEW_ONLY_FIELD_IDS.includes(attribute.attributeId!);
		if (isReviewOnly) return;

		unplacedAttributes.push(meta);
	});

	const hasUnplaced = unplacedAttributes.length > 0;

	const renderUnplacedAttribute = (meta: AttributeMeta) => {
		const { attribute, taskId, taskName, subWorkflowName } = meta;
		const formAttributeId = `${taskId || 'EntityData'}.${attribute.attributeId}`;
		const isOverridable = (attribute as any).isOverridable;
		const displayName = getPlacedFieldLabel({}, attribute, taskName, subWorkflowName);

		return (
			<SNOWFormAttribute
				key={formAttributeId}
				attributeId={formAttributeId}
				attributeName={displayName}
				description={attribute.description}
				dataType={attribute.dataType}
				placeholder={attribute.helpText}
				regex={attribute.regex}
				mandatory={attribute.mandatory !== false}
				options={attribute.options}
				prefetchedValue={attribute.value || undefined}
				handleAutoPopulation={handleAutoPopulation}
				isAutopopulating={autopopulatingAttributes?.has(formAttributeId) || false}
				isOverridable={isOverridable}
			/>
		);
	};

	const unplacedStep: WizardProps.Step | null = hasUnplaced
		? {
			title: 'Additional Fields',
			description: 'These fields were not included in the custom layout but are required by the workflow.',
			content: (
				<Container header={<Header>{`Common Data${workflowName ? ` for ${workflowName}` : ''}`}</Header>}>
					<SpaceBetween size="l">
						{unplacedAttributes.map(renderUnplacedAttribute)}
					</SpaceBetween>
				</Container>
			),
		}
		: null;

	const isUpdateWithDiff = workflowIntention === 'UPDATE' && previousValues && Object.keys(previousValues).length > 0;

	const renderUnplacedReviewSections = (): React.ReactNode => {
		if (!hasUnplaced) return null;

		const header = `Common Data${workflowName ? ` for ${workflowName}` : ''}`;

		// Non-diff review: reuse ColumnLayoutReviewSection with cloned attributes
		// that carry the [TaskName] suffix on their attributeName, so task-specific
		// entries are distinguished inline instead of via their own container.
		if (!isUpdateWithDiff) {
			const displayAttrs = unplacedAttributes.map(({ attribute, taskName, subWorkflowName }) => ({
				...attribute,
				attributeName: getPlacedFieldLabel({}, attribute, taskName, subWorkflowName),
			})) as SnowAttribute[];

			return (
				<ColumnLayoutReviewSection
					key="__unplaced"
					title={header}
					columns={2}
					requirements={displayAttrs}
				/>
			);
		}

		// Diff review: DiffReviewForm scopes previous-value lookups to a single
		// taskId, so we build the diff rows ourselves to mix common + per-task
		// attributes in one container. Skip attributes with a fixed backend value
		// (matches DiffReviewForm's `!req.value` filter).
		return (
			<UnplacedDiffReviewSection
				key="__unplaced"
				header={header}
				items={unplacedAttributes}
				previousValues={previousValues}
			/>
		);
	};

	const reviewStep: WizardProps.Step = {
		title: isUpdateWithDiff
			? `Review Changes for ${workflowName}`
			: `Review Information for ${workflowName}`,
		content: (
			<SpaceBetween size="l">
				{layout.steps.map(step =>
					step.sections.map(section => {
						const sectionAttributes = section.fields
							.map(field => attributeMap.get(field.attributeId))
							.filter(Boolean)
							.map(meta => meta!.attribute);

						if (sectionAttributes.length === 0) return null;

						if (isUpdateWithDiff) {
							return (
								<DiffReviewForm
									key={section.id}
									header={section.title}
									requirements={sectionAttributes}
									previousValues={previousValues}
								/>
							);
						}

						return (
							<ColumnLayoutReviewSection
								key={section.id}
								title={section.title}
								columns={section.columns}
								requirements={sectionAttributes}
							/>
						);
					})
				)}
				{renderUnplacedReviewSections()}
			</SpaceBetween>
		),
	};

	return [...formSteps, ...(unplacedStep ? [unplacedStep] : []), reviewStep];
}
